import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, CornerUpLeft, Save, Trash2, Mail, Clock, FileText, Bot, ShieldAlert, Eye } from "lucide-react";
import OutreachFlags from "./OutreachFlags";
import DiscardDialog from "./DiscardDialog";
import { S, MANROPE, FOCUS, API, STATUS_LABEL, STATUS_TONE, auDate, auDateTime, relative, words } from "./lib";

const Label = ({ children }) => (
  <div className="text-[9.5px] font-bold uppercase tracking-[0.1em] mb-1" style={{ color: S.ink3 }}>
    {children}
  </div>
);

const Btn = ({ children, onClick, testid, tone = "ghost", disabled, title }) => {
  const tones = {
    primary: { background: S.accent, border: `1px solid ${S.accentEdge}`, color: "#000" },
    good: { background: S.good, border: `1px solid ${S.good}`, color: "#fff" },
    ghost: { background: S.card, border: `1px solid ${S.border}`, color: S.ink2 },
    danger: { background: S.card, border: "1px solid #E8C4C4", color: "#8E1212" },
  };
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid} title={title}
      className={`h-9 px-3 rounded-[7px] text-[11.5px] font-bold uppercase tracking-[0.04em] inline-flex items-center gap-1.5 disabled:opacity-50 ${FOCUS}`}
      style={{ ...tones[tone], ...MANROPE }}>
      {children}
    </button>
  );
};

export default function OutreachEmailPanel({ emailId, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [history, setHistory] = useState([]);
  const [discarding, setDiscarding] = useState(false);
  const [preview, setPreview] = useState(null);
  // Anything typed wins over a response that lands afterwards — a slow first
  // read used to arrive mid-sentence and wipe it.
  const typed = useRef(false);

  const fieldsOf = (email) => ({
    subject: email.subject || "", body: email.body || "",
    from_mailbox: email.from_mailbox || "", to_email: email.to_email || "",
    intent: email.intent || "",
  });

  const load = async () => {
    try {
      const [{ data: d }, { data: h }] = await Promise.all([
        axios.get(`${API}/emails/${emailId}`),
        axios.get(`${API}/emails/${emailId}/history`),
      ]);
      setData(d);
      setHistory(h.history || []);
      if (!typed.current) setForm(fieldsOf(d.email));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open that email");
      onClose();
    }
  };

  useEffect(() => { typed.current = false; load(); /* eslint-disable-next-line */ }, [emailId]);

  if (!data) return null;
  const email = data.email;
  const sent = email.status === "sent";
  const tone = STATUS_TONE[email.status] || STATUS_TONE.draft;
  const set = (k, v) => { typed.current = true; setForm((f) => ({ ...f, [k]: v })); };
  const dirty = ["subject", "body", "from_mailbox", "to_email", "intent"]
    .some((k) => (form[k] || "") !== (email[k] || ""));

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      typed.current = false;
      toast.success(msg);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't work");
    } finally { setBusy(false); }
  };

  // Saving trusts the response rather than re-reading: a GET landing after the
  // PUT used to overwrite whatever had been typed in the meantime.
  const save = async () => {
    setBusy(true);
    try {
      const { data: saved } = await axios.put(`${API}/emails/${emailId}`,
        { ...form, expected_version: email.version });
      setData((d) => ({ ...d, email: saved.email }));
      typed.current = false;
      setForm(fieldsOf(saved.email));
      const { data: h } = await axios.get(`${API}/emails/${emailId}/history`);
      setHistory(h.history || []);
      toast.success("Saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't work");
    } finally { setBusy(false); }
  };
  const submitForApproval = async () => {
    if (dirty) await axios.put(`${API}/emails/${emailId}`, { ...form, expected_version: email.version });
    return act(() => axios.post(`${API}/emails/${emailId}/submit`), "Up for approval");
  };
  const approve = (override = false) => act(
    () => axios.post(`${API}/emails/${emailId}/approve`, { override_house_rules: override }),
    "Approved and sent");
  const sendBack = () => act(
    () => axios.post(`${API}/emails/${emailId}/send-back`, { note }), "Sent back with your note");
  const bin = ({ reason, scope, days }) => act(async () => {
    const { data: out } = await axios.post(`${API}/emails/${emailId}/discard`,
      { reason, scope, days });
    setDiscarding(false);
    toast.message(out.note);
    onClose();
  }, "Binned");
  const showPreview = async () => {
    try {
      if (dirty) await axios.put(`${API}/emails/${emailId}`,
        { ...form, expected_version: email.version });
      const { data } = await axios.get(`${API}/emails/${emailId}/preview`);
      setPreview(data);
      if (!data.has_signature) {
        toast.message(`${data.from_email} has no signature yet — it is set up in Sales → Email Templates.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the preview");
    }
  };
  const insertOptOut = () =>
    set("body", `${(form.body || "").trimEnd()}\n\n${data.optout_line}`);

  const blocked = (email.flags || []).filter((f) => f.severity === "block");
  const warns = (email.flags || []).filter((f) => f.severity !== "block");

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="outreach-panel">
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,.42)" }} onClick={onClose} />
      <div className="relative h-full w-full max-w-[1120px] overflow-y-auto"
        style={{ background: S.panel, borderLeft: `1px solid ${S.border}` }}>

        {/* Header */}
        <div className="sticky top-0 z-10 px-5 py-3 flex items-center gap-3"
          style={{ background: S.card, borderBottom: `1px solid ${S.border}` }}>
          <div className="mono text-[12px] font-bold" style={{ color: S.ink }}>{email.ref}</div>
          <span className="px-2 py-[3px] rounded-[5px] text-[10px] font-bold uppercase tracking-[0.08em]"
            style={{ background: tone.bg, border: `1px solid ${tone.edge}`, color: tone.ink }}
            data-testid="outreach-panel-status">
            {STATUS_LABEL[email.status]}
          </span>
          {email.drafted_by_claude && (
            <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: S.accentInk }}>
              <Bot className="w-3.5 h-3.5" /> Drafted by Claude
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Btn onClick={showPreview} testid="outreach-preview" disabled={busy}>
              <Eye className="w-3.5 h-3.5" /> Preview
            </Btn>
            {!sent && <Btn onClick={save} testid="outreach-save" disabled={busy || !dirty}>
              <Save className="w-3.5 h-3.5" /> Save
            </Btn>}
            <button onClick={onClose} data-testid="outreach-close"
              className={`h-9 w-9 rounded-[7px] grid place-items-center ${FOCUS}`}
              style={{ background: S.card, border: `1px solid ${S.border}` }}>
              <X className="w-4 h-4" style={{ color: S.ink2 }} />
            </button>
          </div>
        </div>

        <div className="p-5 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-5">
          {/* The email */}
          <div className="space-y-4">
            <div className="p-4 rounded-[11px] space-y-3"
              style={{ background: S.card, border: `1px solid ${S.border}` }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Goes out as</Label>
                  <select value={form.from_mailbox} onChange={(e) => set("from_mailbox", e.target.value)}
                    disabled={sent} data-testid="outreach-from"
                    className={`w-full h-9 px-2 rounded-[7px] text-[13px] ${FOCUS}`}
                    style={{ background: sent ? S.panel : S.card, border: `1px solid ${S.border}`, color: S.ink }}>
                    <option value="">Choose a mailbox…</option>
                    {(data.mailboxes || []).map((m) => (
                      <option key={m.email} value={m.email}>
                        {m.name ? `${m.name} — ${m.email}` : m.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>To</Label>
                  <input value={form.to_email} onChange={(e) => set("to_email", e.target.value)}
                    disabled={sent} data-testid="outreach-to"
                    className={`w-full h-9 px-2.5 rounded-[7px] text-[13px] ${FOCUS}`}
                    style={{ background: sent ? S.panel : S.card, border: `1px solid ${S.border}`, color: S.ink }} />
                </div>
              </div>
              <div>
                <Label>Subject</Label>
                <input value={form.subject} onChange={(e) => set("subject", e.target.value)}
                  disabled={sent} data-testid="outreach-subject"
                  className={`w-full h-10 px-2.5 rounded-[7px] text-[14px] font-semibold ${FOCUS}`}
                  style={{ background: sent ? S.panel : S.card, border: `1px solid ${S.border}`, color: S.ink }} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Label>Email</Label>
                  <span className="ml-auto text-[10.5px]" style={{ color: S.ink3 }}>
                    {words(form.body)} words
                  </span>
                  {!sent && (
                    <button onClick={insertOptOut} data-testid="outreach-insert-optout"
                      className={`text-[10.5px] font-bold uppercase ${FOCUS}`} style={{ color: S.accentInk }}>
                      + Opt-out line
                    </button>
                  )}
                </div>
                <textarea value={form.body} onChange={(e) => set("body", e.target.value)}
                  disabled={sent} rows={16} data-testid="outreach-body"
                  className={`w-full px-3 py-2.5 rounded-[7px] text-[13.5px] leading-relaxed ${FOCUS}`}
                  style={{ background: sent ? S.panel : S.card, border: `1px solid ${S.border}`, color: S.ink }} />
              </div>
              <div>
                <Label>What this email is for</Label>
                <input value={form.intent} onChange={(e) => set("intent", e.target.value)}
                  disabled={sent} data-testid="outreach-intent"
                  className={`w-full h-9 px-2.5 rounded-[7px] text-[13px] ${FOCUS}`}
                  style={{ background: sent ? S.panel : S.card, border: `1px solid ${S.border}`, color: S.ink }} />
              </div>
            </div>

            {/* Rules */}
            <div className="p-4 rounded-[11px]" style={{ background: S.card, border: `1px solid ${S.border}` }}>
              <div className="flex items-center gap-2 mb-2">
                <ShieldAlert className="w-4 h-4" style={{ color: S.accentInk }} />
                <div className="text-[12.5px] font-bold" style={{ ...MANROPE, color: S.ink }}>House rules</div>
                <button onClick={() => act(() => axios.post(`${API}/emails/${emailId}/check`), "Re-checked")}
                  data-testid="outreach-recheck" disabled={busy}
                  className={`ml-auto text-[10.5px] font-bold uppercase ${FOCUS}`} style={{ color: S.accentInk }}>
                  Re-check
                </button>
              </div>
              <OutreachFlags blocking={blocked} warnings={warns} />
            </div>

            {/* Actions */}
            {!sent && (
              <div className="p-4 rounded-[11px] space-y-3"
                style={{ background: S.card, border: `1px solid ${S.border}` }}>
                <div className="flex flex-wrap items-center gap-2">
                  {email.status === "draft" && (
                    <Btn tone="primary" onClick={submitForApproval} testid="outreach-submit"
                      disabled={busy || blocked.length > 0}
                      title={blocked.length ? "Fix the blocking breaches first" : ""}>
                      <Clock className="w-3.5 h-3.5" /> Up for approval
                    </Btn>
                  )}
                  {email.status === "awaiting_approval" && data.can_approve && (
                    <Btn tone="good" onClick={() => approve(false)} testid="outreach-approve"
                      disabled={busy || blocked.length > 0}
                      title={blocked.length ? "Fix the blocking breaches first" : ""}>
                      <Send className="w-3.5 h-3.5" /> Approve &amp; send
                    </Btn>
                  )}
                  {email.status === "awaiting_approval" && !data.can_approve && (
                    <div className="text-[11.5px]" style={{ color: S.ink2 }}>
                      {data.prospect?.assigned_rep || "The assigned rep"} approves this one.
                    </div>
                  )}
                  <Btn tone="danger" onClick={() => setDiscarding(true)} testid="outreach-delete"
                    disabled={busy}>
                    <Trash2 className="w-3.5 h-3.5" /> Bin it
                  </Btn>
                </div>
                {email.status === "awaiting_approval" && data.can_approve && (
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label>Send it back instead</Label>
                      <input value={note} onChange={(e) => setNote(e.target.value)}
                        placeholder="What needs doing" data-testid="outreach-sendback-note"
                        className={`w-full h-9 px-2.5 rounded-[7px] text-[13px] ${FOCUS}`}
                        style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink }} />
                    </div>
                    <Btn onClick={sendBack} testid="outreach-sendback" disabled={busy || !note.trim()}>
                      <CornerUpLeft className="w-3.5 h-3.5" /> Send back
                    </Btn>
                  </div>
                )}
                {email.sent_back_note && (
                  <div className="px-2.5 py-2 rounded-[7px] text-[11.5px]"
                    style={{ background: "#FFF1EC", border: "1px solid #F0C4B4", color: "#8E3A12" }}
                    data-testid="outreach-sentback-banner">
                    Sent back: {email.sent_back_note}
                  </div>
                )}
                {!data.sending?.saves_to_sent_items && (
                  <div className="text-[11px]" style={{ color: S.ink3 }}>
                    Sent through {data.sending?.provider === "resend" ? "Resend" : "Microsoft 365"}. A copy is BCC'd to the sending mailbox —
                    it will land in their Outlook Sent Items once Microsoft 365 sending is switched on.
                  </div>
                )}
              </div>
            )}

            {email.status === "discarded" && (
              <div className="p-4 rounded-[11px] text-[12px]" data-testid="outreach-discarded-banner"
                style={{ background: "#FFF1EC", border: "1px solid #F0C4B4", color: "#8E3A12" }}>
                Binned by {email.discarded_by} {auDateTime(email.discarded_at)} —
                “{email.discard_reason}”
                {email.discard_scope === "never" && " The prospect is marked do not email."}
                {email.discard_scope === "snooze" && " Outreach to them is paused."}
              </div>
            )}

            {sent && (
              <div className="p-4 rounded-[11px] text-[12px]" style={{ background: "#E3F3E9", border: "1px solid #A9CFBB", color: "#0E6B3F" }}
                data-testid="outreach-sent-banner">
                Approved by {email.approved_by} and sent {auDateTime(email.sent_at)} as{" "}
                {email.from_mailbox}. Logged on {email.prospect_name}'s timeline.
              </div>
            )}
          </div>

          {/* Context */}
          <div className="space-y-4">
            <div className="p-4 rounded-[11px]" style={{ background: S.card, border: `1px solid ${S.border}` }}>
              <div className="text-[13px] font-bold mb-1" style={{ ...MANROPE, color: S.ink }}
                data-testid="outreach-prospect-name">
                {data.prospect?.company_name || "—"}
              </div>
              <div className="text-[11.5px] space-y-0.5" style={{ color: S.ink2 }}>
                <div>{data.prospect?.contact_name || "No contact name"}</div>
                <div className="mono text-[11px]">{data.prospect?.contact_email || "No email"}</div>
                <div>Stage: {data.prospect?.status || "—"}</div>
                <div>Rep: {data.prospect?.assigned_rep || "Unassigned"}</div>
                {data.prospect?.next_action_date && (
                  <div>Next action: {auDate(data.prospect.next_action_date)}</div>
                )}
              </div>
            </div>

            {(data.quotes || []).length > 0 && (
              <div className="p-4 rounded-[11px]" style={{ background: S.card, border: `1px solid ${S.border}` }}>
                <Label>Quotes</Label>
                {data.quotes.map((q) => (
                  <div key={q.id} className="text-[11.5px] py-1" style={{ color: S.ink2 }}>
                    <span className="mono">{q.number || q.id?.slice(0, 8)}</span> · {q.status} ·{" "}
                    {q.total != null ? `${q.currency || ""}${q.total}` : "—"}
                  </div>
                ))}
              </div>
            )}

            <div className="p-4 rounded-[11px]" style={{ background: S.card, border: `1px solid ${S.border}` }}>
              <Label>What has already been said</Label>
              {(data.touches || []).length === 0 && (
                <div className="text-[11.5px]" style={{ color: S.ink3 }}>Nothing logged yet.</div>
              )}
              <div className="space-y-2 max-h-[360px] overflow-y-auto">
                {(data.touches || []).map((t) => (
                  <div key={t.id} className="pb-2" style={{ borderBottom: `1px solid ${S.divider}` }}
                    data-testid={`outreach-touch-${t.id}`}>
                    <div className="flex items-center gap-1.5 text-[10.5px]" style={{ color: S.ink3 }}>
                      {t.kind === "email" ? <Mail className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                      <span className="uppercase font-bold">{t.kind}</span>
                      <span className="ml-auto">{auDate(t.date)}</span>
                    </div>
                    <div className="text-[11.5px] font-semibold" style={{ color: S.ink }}>{t.title || "—"}</div>
                    {t.body && (
                      <div className="text-[11px] mt-0.5 whitespace-pre-wrap" style={{ color: S.ink2 }}>
                        {t.body.slice(0, 260)}{t.body.length > 260 ? "…" : ""}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 rounded-[11px]" style={{ background: S.card, border: `1px solid ${S.border}` }}>
              <Label>Trail</Label>
              <div className="space-y-1.5 max-h-[260px] overflow-y-auto" data-testid="outreach-history">
                {history.map((h) => (
                  <div key={h.id} className="text-[11px]" style={{ color: S.ink2 }}>
                    <span className="font-semibold" style={{ color: S.ink }}>{h.actor}</span>
                    {h.actor_kind === "claude_delegated" && (
                      <span style={{ color: S.accentInk }}>, through Claude</span>
                    )}
                    {" · "}{h.tool}{" · "}{relative(h.at)}
                    {h.reason && <div className="pl-1" style={{ color: S.ink3 }}>“{h.reason}”</div>}
                  </div>
                ))}
                {history.length === 0 && <div className="text-[11px]" style={{ color: S.ink3 }}>—</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
      {preview && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-4" data-testid="outreach-preview-modal">
          <div className="absolute inset-0" style={{ background: "rgba(18,18,18,.5)" }}
            onClick={() => setPreview(null)} />
          <div className="relative w-full max-w-[680px] rounded-[13px] overflow-hidden"
            style={{ background: S.card, border: `1px solid ${S.border}` }}>
            <div className="px-5 py-3" style={{ borderBottom: `1px solid ${S.divider}` }}>
              <div className="flex items-center gap-2">
                <div className="text-[13px] font-bold" style={{ ...MANROPE, color: S.ink }}>
                  What they will see
                </div>
                <button onClick={() => setPreview(null)} data-testid="outreach-preview-close"
                  className={`ml-auto h-8 w-8 rounded-[6px] grid place-items-center ${FOCUS}`}
                  style={{ background: S.panel, border: `1px solid ${S.border}` }}>
                  <X className="w-4 h-4" style={{ color: S.ink2 }} />
                </button>
              </div>
              <div className="text-[11.5px] mt-1" style={{ color: S.ink2 }}>
                From {preview.from_name} &lt;{preview.from_email}&gt; · To {preview.to}
              </div>
              <div className="text-[13px] font-semibold mt-0.5" style={{ color: S.ink }}>
                {preview.subject || "No subject"}
              </div>
              {!preview.has_signature && (
                <div className="text-[11px] mt-1" style={{ color: "#8E3A12" }}>
                  No signature for this mailbox yet — set it up under Sales → Email Templates.
                </div>
              )}
            </div>
            <iframe title="Email preview" srcDoc={preview.html} data-testid="outreach-preview-frame"
              className="w-full" style={{ height: 420, border: 0, background: "#fff" }} />
          </div>
        </div>
      )}

      {discarding && (
        <DiscardDialog email={email} busy={busy} onClose={() => setDiscarding(false)}
          onConfirm={bin} />
      )}
    </div>
  );
}
